import { FC, useState, useMemo, useEffect, useRef } from 'react';
import { Accordion, Button, TextInput, Label } from '../../';
import { StewardReportDetail } from '../../../services/dtos';
import { WeeklyEntryGrid } from './WeeklyEntryGrid';
import { calculateWorkerSummary, formatCurrency, formatHours } from '../../../lib/helpers/stewardReportCalculations';

interface WorkerCardProps {
  detail: StewardReportDetail; // Representative detail (first one)
  allDetails: StewardReportDetail[]; // All detail records for this worker (one per day)
  weekDates: Date[];
  isReadOnly: boolean;
  memberName?: string; // Member name if available
  uniqueMemberId: string;
  onDaySave: (date: Date, uniqueMemberId: string, dayData: { regTimeHours?: number; overTimeHours?: number; doubleTimeHours?: number; showCallQty?: number; mealPenaltyQty?: number; regTimeRate?: number; showCallRate?: number; mealPenaltyRate?: number }) => Promise<void>;
  onDelete: () => void;
  onRatesChangeRef?: React.MutableRefObject<(() => void) | null>; // Ref to call markAllDaysUnsaved in WeeklyEntryGrid
}

export const WorkerCard: FC<WorkerCardProps> = ({
  detail,
  allDetails,
  weekDates,
  isReadOnly,
  memberName,
  uniqueMemberId,
  onDaySave,
  onDelete,
  onRatesChangeRef,
}) => {
  const [isOpen, setIsOpen] = useState(true);

  // Check if rates have been saved (if any detail has rates set, consider them saved)
  const ratesSaved = useMemo(() => {
    return allDetails.some((d) => d.regTimeRate !== undefined && d.regTimeRate !== null && d.regTimeRate > 0);
  }, [allDetails]);

  // Local state for rate input values (start blank if not saved, otherwise use existing rates)
  const [regRateValue, setRegRateValue] = useState<string>(
    ratesSaved ? (detail.regTimeRate || 0).toString() : ''
  );
  const [showCallRateValue, setShowCallRateValue] = useState<string>(
    ratesSaved ? (detail.showCallRate || 0).toString() : ''
  );
  const [mealPenaltyRateValue, setMealPenaltyRateValue] = useState<string>(
    ratesSaved ? (detail.mealPenaltyRate || 0).toString() : ''
  );

  // Track if rates have been saved locally (before API call)
  const [ratesSavedLocally, setRatesSavedLocally] = useState(ratesSaved);

  // Sync local values when detail changes (only if rates were already saved from server, not locally)
  // Do NOT overwrite local state when we just saved locally - preserve those values
  useEffect(() => {
    if (ratesSaved && !ratesSavedLocally) {
      // Only sync from server if rates were saved on server but not locally
      setRegRateValue((detail.regTimeRate || 0).toString());
      setShowCallRateValue((detail.showCallRate || 0).toString());
      setMealPenaltyRateValue((detail.mealPenaltyRate || 0).toString());
    }
  }, [detail.regTimeRate, detail.showCallRate, detail.mealPenaltyRate, ratesSaved]);

  // Calculate OT and DT rates from reg rate (read-only, always calculated)
  const regRate = parseFloat(regRateValue) || 0;
  const otRate = regRate * 1.5;
  const dtRate = regRate * 2;
  const showCallRate = parseFloat(showCallRateValue) || 0;
  const mealPenaltyRate = parseFloat(mealPenaltyRateValue) || 0;

  // Check if rates are valid for saving: reg rate must be > 5, show and meal must be >= 0
  const canSaveRates = regRate > 5 && showCallRate >= 0 && mealPenaltyRate >= 0;

  // Calculate worker summary
  const workerSummary = useMemo(() => {
    return calculateWorkerSummary(allDetails);
  }, [allDetails]);

  // Handle rate change (only updates local state, no validation on change)
  const handleRateChange = (rateType: 'reg' | 'show' | 'meal', value: string) => {
    if (isReadOnly || ratesSavedLocally) return;

    // Allow empty string or valid decimal number
    const regex = /^\d*\.?\d*$/;
    if (value === '' || regex.test(value)) {
      if (rateType === 'reg') {
        setRegRateValue(value);
      } else if (rateType === 'show') {
        setShowCallRateValue(value);
      } else if (rateType === 'meal') {
        setMealPenaltyRateValue(value);
      }
    }
  };

  // Handle rate blur (format value, allow empty for show/meal)
  const handleRateBlur = (rateType: 'reg' | 'show' | 'meal', value: string) => {
    if (isReadOnly || ratesSavedLocally) return;

    const numValue = parseFloat(value) || 0;
    let finalValue = '';

    if (value === '') {
      // Allow empty for show and meal, but reg must have a value
      if (rateType === 'show' || rateType === 'meal') {
        finalValue = '';
      } else {
        finalValue = '';
      }
    } else if (numValue < 0) {
      // Revert to empty on negative
      finalValue = '';
    } else {
      finalValue = numValue.toFixed(2).replace(/\.?0+$/, '');
    }

    if (rateType === 'reg') {
      setRegRateValue(finalValue);
    } else if (rateType === 'show') {
      setShowCallRateValue(finalValue);
    } else if (rateType === 'meal') {
      setMealPenaltyRateValue(finalValue);
    }
  };

  // Handle save rates button click (no API call, just mark as saved locally)
  const handleSaveRates = () => {
    if (!canSaveRates) return;
    setRatesSavedLocally(true);
  };

  return (
    <Accordion alwaysOpen={true}>
      <Accordion.Panel isOpen={isOpen}>
        <style>{`
          .worker-accordion-title button {
            padding-top: 0.25rem !important;
            padding-bottom: 0.25rem !important;
            padding-left: 0.5rem !important;
            padding-right: 0.5rem !important;
          }
        `}</style>
        <div className="flex items-center justify-between w-full pr-1 worker-accordion-title">
          <Accordion.Title className="flex-1">
            <div className="flex items-center gap-1.5">
              <span className="font-medium text-sm">
                {memberName || `Member ${detail.uniqueMemberId.substring(0, 8)}...`}
              </span>
            </div>
          </Accordion.Title>
          {!isReadOnly && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm('Are you sure you want to remove this worker?')) {
                  onDelete();
                }
              }}
              className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors rounded hover:bg-gray-100 dark:hover:bg-gray-700 ml-0.5"
              title="Remove worker"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          )}
        </div>
        <Accordion.Content>
          <div className="space-y-1.5">
            {/* Rates Panel */}
            <div className="bg-gray-50 dark:bg-gray-700 p-1.5 rounded">
              {!ratesSavedLocally ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-1.5">
                    <div>
                      <Label htmlFor={`reg-rate-${detail.id}`} value="Regular Rate *" className="text-xs mb-0" />
                      <TextInput
                        id={`reg-rate-${detail.id}`}
                        type="text"
                        inputMode="decimal"
                        value={regRateValue}
                        onChange={(e) => handleRateChange('reg', e.target.value.trim())}
                        onBlur={(e) => handleRateBlur('reg', e.target.value.trim())}
                        onKeyDown={(e) => {
                          if (
                            ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter'].includes(e.key) ||
                            e.key === '.' ||
                            (e.key >= '0' && e.key <= '9') ||
                            (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown')
                          ) {
                            return;
                          }
                          e.preventDefault();
                        }}
                        className="text-sm py-0.5 px-1.5 h-6"
                        placeholder=""
                      />
                      {regRateValue && regRate <= 5 && (
                        <p className="mt-0 text-xs text-red-600 dark:text-red-400 leading-tight">Must be > 5</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor={`ot-rate-${detail.id}`} value="OT Rate (1.5x)" className="text-xs mb-0" />
                      <TextInput
                        id={`ot-rate-${detail.id}`}
                        type="text"
                        value={otRate > 0 ? otRate.toFixed(2) : ''}
                        disabled={true}
                        readOnly
                        className="text-sm py-0.5 px-1.5 h-6 bg-gray-100 dark:bg-gray-600 cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`dt-rate-${detail.id}`} value="DT Rate (2x)" className="text-xs mb-0" />
                      <TextInput
                        id={`dt-rate-${detail.id}`}
                        type="text"
                        value={dtRate > 0 ? dtRate.toFixed(2) : ''}
                        disabled={true}
                        readOnly
                        className="text-sm py-0.5 px-1.5 h-6 bg-gray-100 dark:bg-gray-600 cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`show-rate-${detail.id}`} value="Show Call Rate" className="text-xs mb-0" />
                      <TextInput
                        id={`show-rate-${detail.id}`}
                        type="text"
                        inputMode="decimal"
                        value={showCallRateValue}
                        onChange={(e) => handleRateChange('show', e.target.value.trim())}
                        onBlur={(e) => handleRateBlur('show', e.target.value.trim())}
                        onKeyDown={(e) => {
                          if (
                            ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter'].includes(e.key) ||
                            e.key === '.' ||
                            (e.key >= '0' && e.key <= '9') ||
                            (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown')
                          ) {
                            return;
                          }
                          e.preventDefault();
                        }}
                        className="text-sm py-0.5 px-1.5 h-6"
                        placeholder=""
                      />
                    </div>
                    <div>
                      <Label htmlFor={`meal-rate-${detail.id}`} value="Meal Penalty Rate" className="text-xs mb-0" />
                      <TextInput
                        id={`meal-rate-${detail.id}`}
                        type="text"
                        inputMode="decimal"
                        value={mealPenaltyRateValue}
                        onChange={(e) => handleRateChange('meal', e.target.value.trim())}
                        onBlur={(e) => handleRateBlur('meal', e.target.value.trim())}
                        onKeyDown={(e) => {
                          if (
                            ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter'].includes(e.key) ||
                            e.key === '.' ||
                            (e.key >= '0' && e.key <= '9') ||
                            (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown')
                          ) {
                            return;
                          }
                          e.preventDefault();
                        }}
                        className="text-sm py-0.5 px-1.5 h-6"
                        placeholder=""
                      />
                    </div>
                  </div>
                  <div className="mt-1">
                    <Button
                      onClick={handleSaveRates}
                      disabled={!canSaveRates}
                      color="blue"
                      size="xs"
                      className="py-1"
                    >
                      Save Rates
                    </Button>
                    {!canSaveRates && regRateValue && (
                      <p className="mt-0 text-xs text-gray-600 dark:text-gray-400 leading-tight">
                        Regular rate must be greater than 5 to save
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-1.5">
                  <div>
                    <Label htmlFor={`reg-rate-${detail.id}`} value="Regular Rate" className="text-xs mb-0" />
                    <div className="text-sm text-gray-900 dark:text-white leading-tight">
                      {regRate.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor={`ot-rate-${detail.id}`} value="OT Rate (1.5x)" className="text-xs mb-0" />
                    <div className="text-sm text-gray-900 dark:text-white leading-tight">
                      {otRate.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor={`dt-rate-${detail.id}`} value="DT Rate (2x)" className="text-xs mb-0" />
                    <div className="text-sm text-gray-900 dark:text-white leading-tight">
                      {dtRate.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor={`show-rate-${detail.id}`} value="Show Call Rate" className="text-xs mb-0" />
                    <div className="text-sm text-gray-900 dark:text-white leading-tight">
                      {showCallRate.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor={`meal-rate-${detail.id}`} value="Meal Penalty Rate" className="text-xs mb-0" />
                    <div className="text-sm text-gray-900 dark:text-white leading-tight">
                      {mealPenaltyRate.toFixed(2)}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Weekly Entry Grid - Show if rates are saved (edit mode) or if we have details (read-only mode) */}
            {(ratesSavedLocally || (isReadOnly && allDetails.length > 0)) && (
              <WeeklyEntryGrid
                allDetails={allDetails}
                weekDates={weekDates}
                isReadOnly={isReadOnly}
                onDaySave={(date, uniqueMemberId, dayData) => onDaySave(date, uniqueMemberId, dayData)}
                uniqueMemberId={uniqueMemberId}
                stewardReportId={detail.stewardReportId}
                currentRates={{
                  regTimeRate: regRate,
                  showCallRate: showCallRate,
                  mealPenaltyRate: mealPenaltyRate,
                }}
                onRatesChangeRef={onRatesChangeRef}
              />
            )}

            {/* Worker Summary */}
            {(ratesSavedLocally || (isReadOnly && allDetails.length > 0)) && (
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <p className="text-sm font-semibold mb-3 dark:text-white">Summary</p>
                <div className="space-y-3">
                  {/* Hours Totals */}
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Reg Hours:</span>
                      <span className="ml-2 font-medium dark:text-white">{formatHours(workerSummary.totalRegHours)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">OT Hours:</span>
                      <span className="ml-2 font-medium dark:text-white">{formatHours(workerSummary.totalOtHours)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">DT Hours:</span>
                      <span className="ml-2 font-medium dark:text-white">{formatHours(workerSummary.totalDtHours)}</span>
                    </div>
                  </div>

                  {/* Quantities */}
                  {(workerSummary.totalShowCallQty > 0 || workerSummary.totalMealPenaltyQty > 0) && (
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {workerSummary.totalShowCallQty > 0 && (
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Show Calls:</span>
                          <span className="ml-2 font-medium dark:text-white">{workerSummary.totalShowCallQty}</span>
                        </div>
                      )}
                      {workerSummary.totalMealPenaltyQty > 0 && (
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Meal Penalties:</span>
                          <span className="ml-2 font-medium dark:text-white">{workerSummary.totalMealPenaltyQty}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Wage Breakdown */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">Regular Wage:</span>
                      <span className="font-medium dark:text-white">{formatCurrency(workerSummary.regWage)}</span>
                    </div>
                    {workerSummary.otWage > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400">Overtime Wage:</span>
                        <span className="font-medium dark:text-white">{formatCurrency(workerSummary.otWage)}</span>
                      </div>
                    )}
                    {workerSummary.dtWage > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400">Double Time Wage:</span>
                        <span className="font-medium dark:text-white">{formatCurrency(workerSummary.dtWage)}</span>
                      </div>
                    )}
                    {workerSummary.showCallWage > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400">Show Call Wage:</span>
                        <span className="font-medium dark:text-white">{formatCurrency(workerSummary.showCallWage)}</span>
                      </div>
                    )}
                    {workerSummary.mealPenaltyWage > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400">Meal Penalty Wage:</span>
                        <span className="font-medium dark:text-white">{formatCurrency(workerSummary.mealPenaltyWage)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm font-semibold border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                      <span className="dark:text-white">Subtotal:</span>
                      <span className="dark:text-white">{formatCurrency(workerSummary.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">Vacation (8%):</span>
                      <span className="font-medium dark:text-white">{formatCurrency(workerSummary.vacation)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-semibold border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                      <span className="dark:text-white">Total Wage:</span>
                      <span className="dark:text-white">{formatCurrency(workerSummary.totalWage)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">Union Fee (3.5%):</span>
                      <span className="font-medium dark:text-white">{formatCurrency(workerSummary.unionFee)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Accordion.Content>
      </Accordion.Panel>
    </Accordion>
  );
};
